import type { ApiError as ApiErrorBody } from '@ili/shared';
import { sessionToken } from '../adapters/session';
import { deviceId } from './device';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public fields?: Record<string, string>,
  ) {
    super(message);
  }
}

/** True when the request failed because there is no connection (not because the server said no). */
export const isNetworkError = (err: unknown) => err instanceof TypeError || (err instanceof ApiError && err.status === 0);

export function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

let csrfPromise: Promise<string> | null = null;
export async function csrfToken(force = false): Promise<string> {
  const c = !force && typeof document !== 'undefined' ? readCookie('ili_csrf') : null;
  if (c) return c;
  csrfPromise ??= fetch('/api/csrf', { credentials: 'same-origin' })
    .then((r) => r.json() as Promise<{ token: string }>)
    .then((j) => j.token)
    .finally(() => (csrfPromise = null));
  return csrfPromise;
}

type Query = Record<string, string | number | boolean | null | undefined>;
export type RequestOptions = { method?: string; body?: unknown; form?: FormData; query?: Query; signal?: AbortSignal };

export function withQuery(path: string, query?: Query): string {
  if (!query) return path;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== '' && v !== false) qs.set(k, String(v));
  const s = qs.toString();
  return s ? `${path}${path.includes('?') ? '&' : '?'}${s}` : path;
}

export async function api<T>(path: string, opts: RequestOptions = {}, retried = false): Promise<T> {
  const method = opts.method ?? (opts.body !== undefined || opts.form ? 'POST' : 'GET');
  const headers: Record<string, string> = { Accept: 'application/json', 'X-Device-Id': deviceId() };
  const bearer = sessionToken.get();
  if (bearer) headers.Authorization = `Bearer ${bearer}`;
  if (method !== 'GET' && method !== 'HEAD' && !bearer) headers['X-CSRF-Token'] = await csrfToken();
  let body: BodyInit | undefined;
  if (opts.form) body = opts.form;
  else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  const res = await fetch(withQuery(`/api${path}`, opts.query), { method, headers, body, credentials: 'same-origin', signal: opts.signal });
  if (res.status === 204) return undefined as T;
  const type = res.headers.get('content-type') ?? '';
  const data = type.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const err = (typeof data === 'object' && data ? data : { error: 'error', message: String(data) }) as ApiErrorBody;
    if (res.status === 403 && err.error === 'csrf' && !retried) {
      await csrfToken(true);
      return api<T>(path, opts, true);
    }
    throw new ApiError(res.status, err.error, err.message || 'Something went wrong.', err.fields);
  }
  return data as T;
}

export const get = <T>(path: string, query?: Query, signal?: AbortSignal) => api<T>(path, { query, signal });
export const post = <T>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body: body ?? {} });
export const put = <T>(path: string, body?: unknown) => api<T>(path, { method: 'PUT', body: body ?? {} });
export const patch = <T>(path: string, body?: unknown) => api<T>(path, { method: 'PATCH', body: body ?? {} });
export const del = <T>(path: string, body?: unknown) => api<T>(path, { method: 'DELETE', body });

/** Human-readable message for any thrown error. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (isNetworkError(err)) return 'You appear to be offline. Check your connection and try again.';
  return (err as Error)?.message || 'Something went wrong.';
}
